import {clsx} from 'clsx'
import React from 'react'
import type {ComponentPropsWithRef, ReactNode, Ref} from 'react'
import {DisclosureContext} from './disclosure-context'
import styles from './DisclosureTrigger.module.css'

// assigns `node` to a ref of either shape (callback function or object with `.current`).
const setRef = <T,>(ref: Ref<T> | undefined, node: T | null): void => {
  if (typeof ref === 'function') ref(node)
  else if (ref) ref.current = node
}

export interface DisclosureTriggerRenderState {
  open: boolean
}

export interface DisclosureTriggerMergedProps {
  id: string
  type: 'button' | 'submit' | 'reset'
  'aria-expanded': boolean
  'aria-controls': string
  tabIndex: number
  onClick: React.MouseEventHandler<HTMLElement>
  ref: React.RefCallback<HTMLElement>
  [key: string]: unknown
}

export interface DisclosureTriggerProps extends ComponentPropsWithRef<'button'> {
  render?:
    | React.ReactElement
    | ((props: DisclosureTriggerMergedProps, state: DisclosureTriggerRenderState) => React.ReactNode)
}

// `ref` is intentionally left in `rest` rather than destructured: the React Compiler types a
// destructured `ref` prop as a ref value and rejects forwarding it to anything but a JSX `ref`
// attribute, which the render prop and `cloneElement` paths below both need to do.
export const DisclosureTrigger = ({
  type = 'button',
  onClick,
  render,
  className,
  children,
  ...rest
}: DisclosureTriggerProps) => {
  const disclosureContextValue = React.use(DisclosureContext)

  if (!disclosureContextValue) throw new Error('Disclosure.Trigger must be rendered inside Disclosure')

  const {ref: triggerCallbackRef, onClick: toggleDisclosure, ...aria} = disclosureContextValue.getTriggerProps()

  // keep `tabIndex` consumer-overridable so patterns such as an accordion's roving tabindex can opt a trigger out of the tab order
  if (rest.tabIndex !== undefined) aria.tabIndex = rest.tabIndex

  const forwardedRef = rest.ref
  const mergedRef: React.RefCallback<HTMLElement> = node => {
    triggerCallbackRef(node)
    setRef(forwardedRef, node)
  }
  const handleClick: React.MouseEventHandler<HTMLElement> = event => {
    onClick?.(event as React.MouseEvent<HTMLButtonElement>)
    if (!event.defaultPrevented) toggleDisclosure()
  }

  if (render === undefined) {
    return (
      <button
        type={type}
        {...rest}
        className={clsx(styles.reset, className)}
        {...aria}
        ref={mergedRef}
        onClick={handleClick}
      >
        {children}
      </button>
    )
  }

  if (typeof render === 'function') {
    return render(
      {...rest, ...aria, type, className, children, onClick: handleClick, ref: mergedRef},
      {open: disclosureContextValue.open},
    )
  }

  const el = render as React.ReactElement<Record<string, unknown>>
  const elProps = el.props
  const elOnClick = elProps.onClick as React.MouseEventHandler<HTMLElement> | undefined
  const elRef = elProps.ref as Ref<HTMLElement> | undefined
  const composedClassName = [className, elProps.className as string | undefined].filter(Boolean).join(' ') || undefined

  const handleElementClick: React.MouseEventHandler<HTMLElement> = event => {
    elOnClick?.(event)
    onClick?.(event as React.MouseEvent<HTMLButtonElement>)
    if (!event.defaultPrevented) toggleDisclosure()
  }

  // eslint-disable-next-line @eslint-react/no-clone-element -- common way to support retroactively merging props onto a JSX element instance
  return React.cloneElement(el, {
    ...rest,
    ...elProps,
    ...aria,
    type: (elProps.type as DisclosureTriggerMergedProps['type']) ?? type,
    tabIndex: (elProps.tabIndex as number | undefined) ?? aria.tabIndex,
    className: composedClassName,
    onClick: handleElementClick,
    ref: (node: HTMLElement | null) => {
      mergedRef(node)
      setRef(elRef, node)
    },
    children: children ?? (elProps.children as ReactNode),
  })
}
